import * as Sentry from "@sentry/react";

import env from "@/utils/env";

const SENTRY_ENABLED_MODES = ["staging", "production"];

function scrubSensitiveData(value) {
	if (value == null) {
		return value;
	}

	if (Array.isArray(value)) {
		return value.map(scrubSensitiveData);
	}

	if (typeof value !== "object") {
		return value;
	}

	return Object.entries(value).reduce((result, [key, nestedValue]) => {
		if (/authorization|password|token|secret|cookie/i.test(key)) {
			result[key] = "[Filtered]";
			return result;
		}

		result[key] = scrubSensitiveData(nestedValue);
		return result;
	}, {});
}

export function isSentryEnabled() {
	const sentryDsn = env.SENTRY_DSN || env.SENTRY_DNS;
	return Boolean(sentryDsn && SENTRY_ENABLED_MODES.includes(env.MODE));
}

export function initSentry() {
	const sentryDsn = env.SENTRY_DSN || env.SENTRY_DNS;
	if (!isSentryEnabled()) {
		return false;
	}

	const releaseVersion = env.VERSION || "unknown";

	Sentry.init({
		dsn: sentryDsn,
		environment: env.SENTRY_ENV || env.MODE,
		release: `agroinventario-front@${releaseVersion}`,
		tracesSampleRate: 0.01,
		integrations: (integrations) => integrations.filter((integration) => integration.name !== "BrowserSession"),
		attachStacktrace: true,
		maxBreadcrumbs: 50,
		ignoreErrors: [
			"ResizeObserver loop limit exceeded",
			"ResizeObserver loop completed with undelivered notifications",
			"msDiscoverChatAvailable",
			"Can't find variable: msDiscoverChatAvailable",
			"Non-Error promise rejection captured with value: undefined",
		],
		beforeSend(event) {
			const eventMessage = event.message || event.exception?.values?.[0]?.value || "";
			const thirdPartyNoise = [
				/msDiscoverChatAvailable/i,
				/MetaMask/i,
				/Failed to read a named property 'print' from 'Window'/i,
				/QuotaExceededError/i,
				/Minified React error #185/i,
				/Minified React error #520/i,
				/Failed to execute 'atob'/i,
				/Request failed with status code 401/i,
			];

			if (thirdPartyNoise.some((pattern) => pattern.test(eventMessage))) {
				return null;
			}

			if (event.request?.headers) {
				event.request.headers = scrubSensitiveData(event.request.headers);
			}

			if (event.extra) {
				event.extra = scrubSensitiveData(event.extra);
			}

			return event;
		},
	});

	Sentry.setTag("app", "agroinventario-front");
	Sentry.setTag("release", releaseVersion);

	return true;
}

export function syncSentryContext({ user, filter, season }) {
	if (!isSentryEnabled()) {
		return;
	}

	if (!user) {
		Sentry.setUser(null);
		Sentry.setContext("workspace", null);
		return;
	}

	Sentry.setUser({
		id: String(user.user_id ?? user.id ?? ""),
		username: user.full_name || user.name || undefined,
	});

	Sentry.setTags({
		customer_id: String(user.customer?.id ?? user.customer_id ?? ""),
		customer_name: user.customer?.name ?? user.customer_name ?? "",
		superuser: String(user.superuser ?? 0),
	});

	Sentry.setContext("workspace", {
		estate_parent_id: filter?.estate_parent_id || "",
		estate_id: filter?.estate_id || "",
		season_id: season?.value ?? filter?.season_id ?? "",
		type_currency: filter?.type_currency || "",
		search: filter?.search || "",
	});
}

export function addApiFailureBreadcrumb(error) {
	if (!isSentryEnabled()) {
		return;
	}

	const response = error?.response;
	const requestConfig = error?.config;

	Sentry.addBreadcrumb({
		category: "api",
		level: "error",
		message: response ? `HTTP ${response.status}` : "Network error",
		data: scrubSensitiveData({
			method: requestConfig?.method?.toUpperCase(),
			url: requestConfig?.url,
			status: response?.status,
			response_data: typeof response?.data === "string" ? response.data.slice(0, 500) : response?.data,
		}),
	});
}

export function captureApiFailure(error) {
	if (!isSentryEnabled()) {
		return;
	}

	const response = error?.response;
	if (!response || response.status < 500) {
		return;
	}

	Sentry.withScope((scope) => {
		scope.setTag("api_status", String(response.status));
		scope.setContext(
			"api",
			scrubSensitiveData({
				method: error.config?.method?.toUpperCase(),
				url: error.config?.url,
				status: response.status,
				data: response.data,
			}),
		);
		Sentry.captureException(error);
	});
}
